import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/** Loads /results, /action-plans, /responses for an assessment and memoizes derived values.
 *  `reloadTick` (any value) bumps the refetch when external changes happen (e.g. signatures saved). */
export default function useAEPData(id, reloadTick = 0) {
  const [data, setData]           = useState(null);
  const [actions, setActions]     = useState([]);
  const [responses, setResponses] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get(`/assessments/${id}/results`),
      api.get(`/assessments/${id}/action-plans`),
      api.get(`/assessments/${id}/responses`),
    ]).then(([r, a, p]) => {
      if (!mounted) return;
      setData(r.data); setActions(a.data); setResponses(p.data);
    });
    return () => { mounted = false; };
  }, [id, reloadTick]);

  const derived = useMemo(() => {
    if (!data) return null;
    const a = data.assessment;
    const company = a.company_snapshot || {};
    const date = new Date().toLocaleDateString("pt-BR");
    const hash = `AEP-${date.replaceAll("/", "")}-${a.id.slice(0, 6).toUpperCase()}`;
    const score100 = Math.round((data.overall_score / 5) * 100);
    const criticalCount = data.questions.filter((q) => q.score >= 4.5).length;
    const ranked = [...data.dimensions]
      .filter((d) => d.responses > 0)
      .sort((x, y) => y.score - x.score);
    const focoDim = ranked[0]?.dimensao || "";
    const critByDim = {};
    data.questions.forEach((q) => {
      if (q.score >= 4) critByDim[q.dimensao] = (critByDim[q.dimensao] || 0) + 1;
    });
    const maxResp = Math.max(...ranked.map((d) => d.responses), 1);
    return { a, company, date, hash, score100, criticalCount, ranked, focoDim, critByDim, maxResp };
  }, [data]);

  const timelineBuckets = useMemo(() => {
    const buckets = { "30 dias": 0, "60 dias": 0, "90 dias": 0, "180 dias": 0 };
    actions.forEach((act) => {
      if (!act.prazo) return;
      const diff = (new Date(act.prazo) - new Date()) / 86400000;
      if (diff <= 30) buckets["30 dias"] += 1;
      else if (diff <= 60) buckets["60 dias"] += 1;
      else if (diff <= 90) buckets["90 dias"] += 1;
      else if (diff <= 180) buckets["180 dias"] += 1;
    });
    return buckets;
  }, [actions]);

  return { data, actions, responses, derived, timelineBuckets };
}
